import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import type { NavLinkScope, SavedNavLink } from '../types';

export const NAV_LINKS_QUERY_KEY = 'nav-links';

function getNavLinksQueryKey(tenantId: string | undefined, scope: NavLinkScope) {
  return [NAV_LINKS_QUERY_KEY, tenantId, scope] as const;
}

export function useNavLinks(scope: NavLinkScope) {
  const { user } = useAuth();
  const tenantId = user?.tenantId;

  return useQuery({
    queryKey: getNavLinksQueryKey(tenantId, scope),
    queryFn: async (): Promise<SavedNavLink[]> => {
      if (!tenantId) return [];

      switch (scope) {
        case 'User':
          return apiClient.getMyNavLinks(tenantId);
        case 'Tenant':
          return apiClient.getTenantNavLinks(tenantId);
        case 'System':
          return apiClient.getSystemNavLinks(tenantId);
        default:
          return [];
      }
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAllNavLinks() {
  const userLinks = useNavLinks('User');
  const tenantLinks = useNavLinks('Tenant');
  const systemLinks = useNavLinks('System');

  return {
    data: [
      ...(userLinks.data ?? []),
      ...(tenantLinks.data ?? []),
      ...(systemLinks.data ?? []),
    ],
    isLoading: userLinks.isLoading || tenantLinks.isLoading || systemLinks.isLoading,
    isError: userLinks.isError || tenantLinks.isError || systemLinks.isError,
  };
}

export function useInvalidateNavLinks() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const tenantId = user?.tenantId;

  return (scope?: NavLinkScope) => {
    if (scope && tenantId) {
      queryClient.invalidateQueries({
        queryKey: getNavLinksQueryKey(tenantId, scope),
      });
    }

    queryClient.invalidateQueries({ queryKey: [NAV_LINKS_QUERY_KEY] });
  };
}
